/* eslint-disable react/jsx-no-bind, max-len, no-undefined, react/no-unused-prop-types */
import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {FormattedMessage, injectIntl, intlShape} from 'react-intl';
import {AutoSizer, List} from 'react-virtualized';
import SerialPlotter from './serial-plotter.jsx';
import styles from './serial-monitor.css';

const SerialMonitorComponent = ({
    expanded,
    connected,
    error,
    messages,
    inputText,
    baudRate,
    showTimestamp,
    autoScroll,
    isPaused,
    searchQuery,
    lineEnding,
    viewMode,
    rxCount,
    txCount,
    onBaudRateChange,
    onLineEndingChange,
    onSearchChange,
    onToggleTimestamp,
    onToggleAutoScroll,
    onTogglePause,
    onToggleViewMode,
    onCopyAll,
    onSaveLog,
    onToggle,
    onConnect,
    onDisconnect,
    onInputChange,
    onKeyPress,
    onSend,
    onClear,
    terminalRef,
    isChangingBaudRate
}) => {
    // Filter messages based on search query (case-insensitive)
    const filteredMessages = useMemo(() => {
        if (!searchQuery) return messages;
        const lowerQuery = searchQuery.toLowerCase();
        return messages.filter(m => m.content.toLowerCase().includes(lowerQuery));
    }, [messages, searchQuery]);

    const handleConsoleClick = () => onToggleViewMode('console');
    const handlePlotterClick = () => onToggleViewMode('plotter');

    const rowRenderer = ({index, key, style}) => {
        const msg = filteredMessages[index];
        let prefix = '';
        let colorClass = '';
        
        if (msg.type === 'incoming') {
            prefix = '<- ';
            colorClass = styles.msgIncoming;
        } else if (msg.type === 'outgoing') {
            prefix = '-> ';
            colorClass = styles.msgOutgoing;
        } else if (msg.type === 'system') {
            prefix = '[sys] ';
            colorClass = styles.msgSystem;
        } else if (msg.type === 'warning') {
            prefix = '[warn] ';
            colorClass = styles.msgWarning;
        } else if (msg.type === 'error') {
            prefix = '[err] ';
            colorClass = styles.msgError;
        }

        let timeStr = '';
        if (showTimestamp && msg.timestamp) {
            const date = new Date(msg.timestamp);
            const pad = n => n.toString().padStart(2, '0');
            timeStr = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${date.getMilliseconds().toString()
                .padStart(3, '0')} `;
        }

        const countSuffix = msg.count > 1 ? ` ×${msg.count}` : '';

        return (
            <div
                key={key}
                style={style}
                className={classNames(styles.messageRow, colorClass)}
            >
                {timeStr}{prefix}{msg.content}{countSuffix}
            </div>
        );
    };

    return (
        <div className={styles.serialMonitorContainer}>
            <div
                className={classNames(styles.serialMonitorHeader, expanded ? styles.headerExpanded : null)}
                onClick={onToggle}
            >
                <FormattedMessage
                    defaultMessage="Serial Monitor"
                    description="Button to open the serial monitor"
                    id="gui.serialMonitor.header"
                />
                <span className={styles.headerIcon}>
                    {expanded ? '▼' : '▲'}
                </span>
            </div>
            {expanded ? (
                <div className={styles.serialMonitorBody}>
                    
                    {/* Top Toolbar */}
                    <div className={styles.topToolbar}>
                        <div className={styles.statusGroup}>
                            {isChangingBaudRate ? (
                                <span className={styles.statusConnected}>{'Changing baud rate...'}</span>
                            ) : connected ? (
                                <span className={styles.statusConnected}>{'Connected to Device'}</span>
                            ) : (
                                <span className={styles.statusDisconnected}>{'Disconnected'}</span>
                            )}
                        </div>
                        
                        <div className={styles.controlsGroup}>
                            <select
                                className={styles.selectInput}
                                value={baudRate}
                                onChange={onBaudRateChange}
                                disabled={isChangingBaudRate}
                            >
                                <option value="9600">{'9600 baud'}</option>
                                <option value="19200">{'19200 baud'}</option>
                                <option value="38400">{'38400 baud'}</option>
                                <option value="57600">{'57600 baud'}</option>
                                <option value="115200">{'115200 baud'}</option>
                            </select>

                            <select
                                className={styles.selectInput}
                                value={lineEnding}
                                onChange={onLineEndingChange}
                            >
                                <option value="none">{'No line ending'}</option>
                                <option value="nl">{'Newline (NL)'}</option>
                                <option value="cr">{'Carriage Return (CR)'}</option>
                                <option value="nl_cr">{'Both NL & CR'}</option>
                            </select>

                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={showTimestamp}
                                    onChange={onToggleTimestamp}
                                />
                                {'Timestamp'}
                            </label>

                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={autoScroll}
                                    onChange={onToggleAutoScroll}
                                />
                                {'Auto Scroll'}
                            </label>

                            <label className={classNames(styles.checkboxLabel, isPaused ? styles.pausedActive : '')}>
                                <input
                                    type="checkbox"
                                    checked={isPaused}
                                    onChange={onTogglePause}
                                />
                                {isPaused ? 'Resume' : 'Pause'}
                            </label>

                            <div className={styles.searchWrapper}>
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder="Search..."
                                    value={searchQuery}
                                    onChange={onSearchChange}
                                />
                            </div>

                            {/* View Mode Toggle */}
                            <div className={styles.viewModeToggle}>
                                <button
                                    className={classNames(
                                        styles.toggleBtn,
                                        viewMode === 'console' ? styles.active : ''
                                    )}
                                    onClick={handleConsoleClick}
                                >
                                    <FormattedMessage
                                        defaultMessage="Console"
                                        description="Switch to console"
                                        id="gui.serialMonitor.consoleMode"
                                    />
                                </button>
                                <button
                                    className={classNames(
                                        styles.toggleBtn,
                                        viewMode === 'plotter' ? styles.active : ''
                                    )}
                                    onClick={handlePlotterClick}
                                >
                                    <FormattedMessage
                                        defaultMessage="Plotter"
                                        description="Switch to plotter"
                                        id="gui.serialMonitor.plotterMode"
                                    />
                                </button>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className={styles.errorMessage}>
                            {error}
                        </div>
                    )}
                    
                    {/* Output Area */}
                    <div className={styles.terminalOutput}>
                        {messages.length === 0 && connected && viewMode === 'console' ? (
                            <div className={styles.emptyState}>{'Waiting for serial data...'}</div>
                        ) : viewMode === 'plotter' ? (
                            <SerialPlotter messages={messages} />
                        ) : (
                            <AutoSizer>
                                {({height, width}) => (
                                    <List
                                        ref={terminalRef}
                                        width={width}
                                        height={height}
                                        rowCount={filteredMessages.length}
                                        rowHeight={20} // Fixed height for each line to enable horizontal scrolling natively
                                        rowRenderer={rowRenderer}
                                        scrollToIndex={autoScroll ? filteredMessages.length - 1 : undefined}
                                        style={{overflowX: 'auto', overflowY: 'auto'}}
                                    />
                                )}
                            </AutoSizer>
                        )}
                    </div>
                    
                    {/* Bottom Toolbar */}
                    <div className={styles.bottomToolbar}>
                        <div className={styles.statsGroup}>
                            <span className={styles.statLabel}>{'RX: '}{rxCount}</span>
                            <span className={styles.statLabel}>{'TX: '}{txCount}</span>
                        </div>

                        <input
                            type="text"
                            className={styles.terminalInput}
                            value={inputText}
                            onChange={onInputChange}
                            onKeyPress={onKeyPress}
                            placeholder="Type message and press Enter..."
                            disabled={!connected || isChangingBaudRate}
                        />

                        <div className={styles.actionButtons}>
                            <button
                                className={classNames(styles.btnModern, styles.primaryButton)}
                                onClick={onSend}
                                disabled={!connected || isChangingBaudRate}
                            >
                                {'Send'}
                            </button>
                            <div className={styles.divider} />
                            <button
                                className={classNames(styles.btnModern, styles.secondaryButton)}
                                onClick={onCopyAll}
                                disabled={messages.length === 0}
                                title="Copy All"
                            >
                                {'Copy'}
                            </button>
                            <button
                                className={classNames(styles.btnModern, styles.secondaryButton)}
                                onClick={onSaveLog}
                                disabled={messages.length === 0}
                                title="Save Log"
                            >
                                {'Save Log'}
                            </button>
                            <button
                                className={classNames(styles.btnModern, styles.secondaryButton)}
                                onClick={onClear}
                                title="Clear Logs"
                            >
                                {'Clear'}
                            </button>
                            <div className={styles.divider} />
                            {connected ? (
                                <button
                                    className={classNames(styles.btnModern, styles.dangerButton)}
                                    onClick={onDisconnect}
                                    disabled={isChangingBaudRate}
                                >
                                    {'Disconnect'}
                                </button>
                            ) : (
                                <button
                                    className={classNames(styles.btnModern, styles.successButton)}
                                    onClick={onConnect}
                                    disabled={isChangingBaudRate}
                                >
                                    {'Connect'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

SerialMonitorComponent.propTypes = {
    baudRate: PropTypes.string,
    connected: PropTypes.bool,
    error: PropTypes.string,
    expanded: PropTypes.bool,
    inputText: PropTypes.string,
    intl: intlShape,
    messages: PropTypes.arrayOf(PropTypes.object),
    showTimestamp: PropTypes.bool,
    autoScroll: PropTypes.bool,
    isPaused: PropTypes.bool,
    searchQuery: PropTypes.string,
    lineEnding: PropTypes.string,
    viewMode: PropTypes.string,
    rxCount: PropTypes.number,
    txCount: PropTypes.number,
    onBaudRateChange: PropTypes.func,
    onLineEndingChange: PropTypes.func,
    onSearchChange: PropTypes.func,
    onToggleTimestamp: PropTypes.func,
    onToggleAutoScroll: PropTypes.func,
    onTogglePause: PropTypes.func,
    onToggleViewMode: PropTypes.func,
    onCopyAll: PropTypes.func,
    onSaveLog: PropTypes.func,
    onConnect: PropTypes.func,
    onDisconnect: PropTypes.func,
    onInputChange: PropTypes.func,
    onKeyPress: PropTypes.func,
    onSend: PropTypes.func,
    onClear: PropTypes.func,
    onToggle: PropTypes.func,
    terminalRef: PropTypes.func,
    isChangingBaudRate: PropTypes.bool
};

export default injectIntl(SerialMonitorComponent);
